import { useState, useEffect, useCallback, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { Dialog, DialogContent } from "@/components/ui/dialog";
import UserMessage from "@/components/chat/UserMessage";
import SimpleChatInput from "@/components/chat/SimpleChatInput";
import { useToast } from "@/hooks/use-toast";
import { useAuth } from "@/contexts/AuthContext";
import { config } from "@/lib/config";
import { Brain, Loader2, AlertCircle } from "lucide-react";

interface MessageEntry {
  id: string;
  role: "user" | "agent";
  content: string;
  lessonPlan?: LessonPlanPreview;
  thinkingTime?: number;
  suggestions?: Array<{
    title: string;
    description: string;
  }>;
  followUpPrompt?: string;
}
interface IntentAnalysis {
  topic: string;
  inferred_level: string;
  estimated_duration: number;
  emphasis: {
    concepts: number;
    practice: number;
    assessment: number;
  };
  focus_areas: string[];
  intent_notes: string;
}

interface LessonConcept {
  id: string;
  title: string;
  focus: string;
  minutes: number;
  tags?: string[];
}

interface LessonPlan {
  lesson_title: string;
  subtitle: string;
  section_minutes: {
    intro: number;
    learn: number;
    practice: number;
    test: number;
    next_steps: number;
  };
  concepts: LessonConcept[];
  knowledge_check_count: number;
  faq_count: number;
  spot_mistake_count: number;
  match_pair_count: number;
  mcq_count: number;
  fill_blank_count: number;
  true_false_count: number;
  misconceptions_to_target: string[];
  notes: string;
}

interface ApiResponse {
  response_type: "conversation" | "lesson_plan" | "knowledge_answer";
  message?: string;  // For conversational responses
  classification?: string;
  // Knowledge answer fields (conditional):
  answer?: string;
  suggestions?: Array<{
    title: string;
    description: string;
  }>;
  follow_up_prompt?: string;
  // Lesson plan fields (conditional):
  success?: boolean;
  intent_analysis?: IntentAnalysis;
  plan?: LessonPlan;
  sources?: any[];
  foundation_data?: any;
  pedagogical_framework?: any;
}

interface LessonPlanPreview {
  success: boolean;
  intent_analysis: IntentAnalysis;
  plan: LessonPlan;
  sources: any[];
  foundation_data: any;
  pedagogical_framework?: any;
}

interface TopicOnboardingModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  initialMessage?: string;
  topicId?: string;
  onFlowComplete?: () => void;
}

const TopicOnboardingModal = ({
  open,
  onOpenChange,
  initialMessage = "",
  topicId,
  onFlowComplete,
}: TopicOnboardingModalProps) => {
  const { toast } = useToast();
  const { session } = useAuth();
  const navigate = useNavigate();

  const [messageHistory, setMessageHistory] = useState<MessageEntry[]>([]);
  const [lessonPlanPreview, setLessonPlanPreview] = useState<LessonPlanPreview | null>(null);
  const [isGenerating, setIsGenerating] = useState(false);
  const [statusMessage, setStatusMessage] = useState<string | null>(null);
  const [isAccepting, setIsAccepting] = useState(false);
  const [acceptStatus, setAcceptStatus] = useState<string | null>(null);
  const [acceptError, setAcceptError] = useState<string | null>(null);
  const [bootstrapComplete, setBootstrapComplete] = useState(false);
  const [generationProgress, setGenerationProgress] = useState(0);
  const [initialPromptProcessed, setInitialPromptProcessed] = useState(false);

  const messagesEndRef = useRef<HTMLDivElement>(null);

  const initialPrompt = initialMessage.trim();

  const resetState = useCallback(() => {
    setMessageHistory([]);
    setIsGenerating(false);
    setStatusMessage(null);
    setIsAccepting(false);
    setAcceptStatus(null);
    setAcceptError(null);
    setBootstrapComplete(false);
    setLessonPlanPreview(null);
    setGenerationProgress(0);
    setInitialPromptProcessed(false);
  }, []);
  const generateLessonPlan = useCallback(
    async (message: string) => {
      const trimmed = message.trim();
      if (!trimmed || !session?.access_token) return;

      setIsGenerating(true);
      setStatusMessage("Analyzing your request...");

      try {
        // Build conversation history from messageHistory
        const conversationHistory = messageHistory
          .filter(msg => msg.role === "user" || msg.role === "agent")
          .map(msg => ({
            role: msg.role,
            content: msg.content
          }));

        const response = await fetch(`${config.BACKEND_API_URL}/api/lessons/preview`, {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${session.access_token}`,
          },
          body: JSON.stringify({ 
            student_request: trimmed,
            conversation_history: conversationHistory
          }),
        });

        if (!response.ok) {
          throw new Error(`Failed to generate lesson plan: ${response.statusText}`);
        }

        const data: ApiResponse = await response.json();

        // Handle conversational responses
        if (data.response_type === "conversation") {
          setMessageHistory((prev) => [
            ...prev,
            {
              id: `agent-${Date.now()}`,
              role: "agent",
              content: data.message || "How can I help you learn today?",
            },
          ]);
          setStatusMessage(null);
          setIsGenerating(false);
          setInitialPromptProcessed(true); // Mark as processed to prevent retry loop
          return;
        }

        // Handle knowledge answer responses (question + suggestions)
        if (data.response_type === "knowledge_answer") {
          setMessageHistory((prev) => [
            ...prev,
            {
              id: `agent-${Date.now()}`,
              role: "agent",
              content: data.answer || "Here's what I found...",
              suggestions: data.suggestions || [],
              followUpPrompt: data.follow_up_prompt,
            },
          ]);
          setStatusMessage(null);
          setIsGenerating(false);
          setInitialPromptProcessed(true); // Mark as processed to prevent retry loop
          return;
        }

        // Handle lesson plan responses
        if (data.response_type === "lesson_plan" && data.plan) {
          const lessonData: LessonPlanPreview = {
            success: data.success || true,
            intent_analysis: data.intent_analysis!,
            plan: data.plan,
            sources: data.sources || [],
            foundation_data: data.foundation_data || {},
            pedagogical_framework: data.pedagogical_framework
          };
          
          setLessonPlanPreview(lessonData);
          
          // Add lesson plan to message history
          setMessageHistory((prev) => [
            ...prev,
            {
              id: `agent-${Date.now()}`,
              role: "agent",
              content: "Here's your personalized lesson plan",
              lessonPlan: lessonData,
              thinkingTime: 10,
            },
          ]);

          setStatusMessage(null);
          setInitialPromptProcessed(true); // Mark as processed
        } else {
          throw new Error("Invalid response format");
        }
      } catch (error) {
        console.error("Failed to generate lesson plan:", error);
        toast({
          title: "Could not create lesson plan",
          description: error instanceof Error ? error.message : "Something went wrong",
          variant: "destructive",
        });
        setStatusMessage("We hit a snag. Please try again.");
      } finally {
        setIsGenerating(false);
      }
    },
    [session?.access_token, toast, messageHistory]
  );

  useEffect(() => {
    if (!open) {
      resetState();
      return;
    }

    if (!bootstrapComplete && initialPrompt) {
      const trimmed = initialPrompt;
      setMessageHistory([
        {
          id: `user-${Date.now()}`,
          role: "user",
          content: trimmed,
        },
      ]);
      setBootstrapComplete(true);
    }
  }, [open, initialPrompt, bootstrapComplete, resetState]);

  useEffect(() => {
    if (open && bootstrapComplete && initialPrompt && !lessonPlanPreview && !isGenerating && !initialPromptProcessed) {
      generateLessonPlan(initialPrompt);
    }
  }, [open, bootstrapComplete, initialPrompt, lessonPlanPreview, isGenerating, initialPromptProcessed, generateLessonPlan]);

  useEffect(() => {
    // Smooth scroll with a slight delay for better UX
    const timer = setTimeout(() => {
      messagesEndRef.current?.scrollIntoView({ behavior: "smooth", block: "end" });
    }, 100);
    return () => clearTimeout(timer);
  }, [messageHistory, isGenerating, isAccepting]);

  const handleChatSend = async (message: string) => {
    const trimmed = message.trim();
    if (!trimmed || isGenerating || isAccepting) {
      return;
    }

    setMessageHistory((prev) => [
      ...prev,
      {
        id: `user-${Date.now()}`,
        role: "user",
        content: trimmed,
      },
    ]);

    if (!session?.access_token) {
      toast({
        title: "Sign in required",
        description: "Please log in to continue.",
        variant: "destructive",
      });
      return;
    }

    // If no lesson plan preview exists, generate a new one
    if (!lessonPlanPreview) {
      await generateLessonPlan(trimmed);
      return;
    }

    // Otherwise, refine existing lesson plan
    setIsGenerating(true);
    setStatusMessage("Refining your lesson plan...");

    try {
      const refinedRequest = `${initialMessage}\n\nRefinement: ${trimmed}`;
      
      // Build conversation history including the refinement request
      const conversationHistory = messageHistory
        .filter(msg => msg.role === "user" || msg.role === "agent")
        .map(msg => ({
          role: msg.role,
          content: msg.content
        }));
      
      const response = await fetch(`${config.BACKEND_API_URL}/api/lessons/preview`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${session.access_token}`,
        },
        body: JSON.stringify({ 
          student_request: refinedRequest,
          conversation_history: conversationHistory
        }),
      });

      if (!response.ok) {
        throw new Error(`Failed to refine lesson plan: ${response.statusText}`);
      }

      const data: ApiResponse = await response.json();

      // Handle conversational responses during refinement
      if (data.response_type === "conversation") {
        setMessageHistory((prev) => [
          ...prev,
          {
            id: `agent-${Date.now()}`,
            role: "agent",
            content: data.message || "I've updated your lesson plan",
          },
        ]);
        setStatusMessage(null);
        setIsGenerating(false);
        return;
      }

      // Handle knowledge answer responses during refinement
      if (data.response_type === "knowledge_answer") {
        setMessageHistory((prev) => [
          ...prev,
          {
            id: `agent-${Date.now()}`,
            role: "agent",
            content: data.answer || "Here's what I found...",
            suggestions: data.suggestions || [],
            followUpPrompt: data.follow_up_prompt,
          },
        ]);
        setStatusMessage(null);
        setIsGenerating(false);
        return;
      }

      // Handle lesson plan responses
      if (data.response_type === "lesson_plan" && data.plan) {
        const lessonData: LessonPlanPreview = {
          success: data.success || true,
          intent_analysis: data.intent_analysis!,
          plan: data.plan,
          sources: data.sources || [],
          foundation_data: data.foundation_data || {},
          pedagogical_framework: data.pedagogical_framework
        };
        
        setLessonPlanPreview(lessonData);
        
        setMessageHistory((prev) => [
          ...prev,
          {
            id: `agent-${Date.now()}`,
            role: "agent",
            content: "I've updated your lesson plan",
            lessonPlan: lessonData,
            thinkingTime: 8,
          },
        ]);
      }

      setStatusMessage(null);
    } catch (error) {
      console.error("Failed to refine lesson plan:", error);
      toast({
        title: "Could not refine lesson plan",
        description: error instanceof Error ? error.message : "Please try again",
        variant: "destructive",
      });
      setStatusMessage("We couldn't update the plan. Try another tweak.");
    } finally {
      setIsGenerating(false);
    }
  };
  const handleLessonAccept = async () => {
    if (!session?.access_token || !lessonPlanPreview) {
      toast({
        title: "Lesson plan not ready",
        description: "Please wait until the lesson plan is generated",
        variant: "destructive",
      });
      return;
    }

    setIsAccepting(true);
    setAcceptError(null);

    try {
      let finalTopicId: string;
      let lessonId: string;

      if (topicId) {
        // Add lesson to existing topic
        const lessonResponse = await fetch(
          `${config.BACKEND_API_URL}/api/topics/${topicId}/lessons`,
          {
            method: "POST",
            headers: {
              "Content-Type": "application/json",
              Authorization: `Bearer ${session.access_token}`,
            },
            body: JSON.stringify({
              lesson_plan_data: lessonPlanPreview,
            }),
          }
        );

        if (!lessonResponse.ok) {
          throw new Error(`Failed to add lesson: ${lessonResponse.statusText}`);
        }

        const data = await lessonResponse.json();
        finalTopicId = topicId;
        lessonId = data.lesson_id || data.id;
        
        // For existing topic: close modal immediately, show toast
        toast({
          title: "Lesson added!",
          description: "Your lesson is being generated in the background",
        });
        
        onFlowComplete?.();
        onOpenChange(false);
      } else {
        // Create new topic with lesson plan
        const topicResponse = await fetch(`${config.BACKEND_API_URL}/api/topics`, {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${session.access_token}`,
          },
          body: JSON.stringify({
            title: lessonPlanPreview.plan.lesson_title,
            lesson_plan_data: lessonPlanPreview,
          }),
        });

        if (!topicResponse.ok) {
          throw new Error(`Failed to create topic: ${topicResponse.statusText}`);
        }

        const data = await topicResponse.json();
        finalTopicId = data.id;
        lessonId = data.lesson_id;
        
        // For new topic: navigate immediately, show toast
        toast({
          title: "Topic created!",
          description: "Your lesson is being generated",
        });
        
        onFlowComplete?.();
        navigate(`/topic/${finalTopicId}`);
        onOpenChange(false);
      }
    } catch (error) {
      console.error("Failed to accept lesson plan:", error);
      const message = error instanceof Error ? error.message : "Something went wrong";
      setAcceptError(message);
      toast({
        title: "Unable to start lesson",
        description: message,
        variant: "destructive",
      });
    } finally {
      setIsAccepting(false);
    }
  };

  const handleDialogChange = (nextOpen: boolean) => {
    if (!nextOpen && isAccepting) {
      toast({
        title: "Please wait",
        description: "We're still preparing your lesson.",
      });
      return;
    }

    if (!nextOpen) {
      resetState();
    }

    onOpenChange(nextOpen);
  };

  return (
    <Dialog open={open} onOpenChange={handleDialogChange}>
      <DialogContent
        className="fixed left-0 top-0 flex flex-col h-screen w-screen max-w-none translate-x-0 translate-y-0 overflow-hidden rounded-none border-0 bg-[#FAFAFA] p-0"
        hideCloseButton
      >
        <button
          onClick={() => handleDialogChange(false)}
          disabled={isAccepting}
          className="absolute top-4 right-4 w-9 h-9 rounded-full flex items-center justify-center text-gray-600 hover:bg-gray-100 disabled:opacity-50 disabled:cursor-not-allowed z-10"
        >
          ×
        </button>
        
        {/* Fixed Header */}
        <div className="pt-8 pb-4 flex-shrink-0 flex justify-center bg-[#FAFAFA]">
          <div className="w-full max-w-4xl px-6">
            <h2 className="text-lg font-semibold" style={{ fontFamily: "Lexend Deca" }}>
              {topicId ? "Add a new lesson" : "What would you like to learn today?"}
            </h2>
          </div>
        </div>

        {/* Scrollable Thread Container */}
        <div className="flex-1 overflow-y-auto min-h-0 bg-[#FAFAFA] relative">
          <div className="flex flex-col items-center w-full pt-6">
            <div className="w-full max-w-4xl px-6 space-y-6">
                  {messageHistory.map((message, index) =>
                    message.role === "user" ? (
                      <div 
                        key={message.id} 
                        className="animate-in fade-in slide-in-from-bottom-4 duration-500"
                        style={{ animationDelay: `${index * 50}ms`, animationFillMode: 'backwards' }}
                      >
                        <UserMessage message={message.content} />
                      </div>
                    ) : message.role === "agent" && message.suggestions && message.suggestions.length > 0 ? (
                      // Knowledge answer with suggestions
                      <div 
                        key={message.id} 
                        className="flex justify-start animate-in fade-in slide-in-from-bottom-4 duration-500"
                        style={{ animationDelay: `${index * 50}ms`, animationFillMode: 'backwards' }}
                      >
                        <div className="bg-white rounded-2xl px-5 py-4 max-w-[85%]">
                          {/* Direct answer */}
                          <p className="text-sm text-gray-800 mb-4" style={{ fontFamily: "Lexend Deca" }}>
                            {message.content}
                          </p>
                          
                          {/* Follow-up prompt */}
                          {message.followUpPrompt && (
                            <p className="text-sm text-gray-600 mb-3 italic" style={{ fontFamily: "Lexend Deca" }}>
                              {message.followUpPrompt}
                            </p>
                          )}
                          
                          {/* Clickable suggestions */}
                          <div className="space-y-2">
                            {message.suggestions.map((suggestion, idx) => (
                              <button
                                key={idx}
                                onClick={() => {
                                  const suggestionPrompt = `Create a lesson: ${suggestion.title}. ${suggestion.description}`;
                                  generateLessonPlan(suggestionPrompt);
                                }}
                                className="w-full text-left bg-gray-50 hover:bg-gray-100 rounded-lg p-3 transition-colors group"
                              >
                                <p className="font-medium text-sm text-gray-900 group-hover:text-gray-950" style={{ fontFamily: "Lexend Deca" }}>
                                  {suggestion.title}
                                </p>
                                <p className="text-xs text-gray-600 mt-0.5" style={{ fontFamily: "Lexend Deca" }}>
                                  {suggestion.description}
                                </p>
                              </button>
                            ))}
                          </div>
                        </div>
                      </div>
                    ) : message.role === "agent" && !message.lessonPlan ? (
                      // Regular agent message (conversational response)
                      <div 
                        key={message.id} 
                        className="flex justify-start animate-in fade-in slide-in-from-bottom-4 duration-500"
                        style={{ animationDelay: `${index * 50}ms`, animationFillMode: 'backwards' }}
                      >
                        <div className="bg-white rounded-2xl px-5 py-4 max-w-[75%]">
                          <p className="text-sm text-gray-800" style={{ fontFamily: "Lexend Deca" }}>
                            {message.content}
                          </p>
                        </div>
                      </div>
                    ) : message.lessonPlan ? (
                      <div 
                        key={message.id} 
                        className="flex justify-start animate-in fade-in slide-in-from-bottom-4 duration-700"
                        style={{ animationDelay: `${index * 50}ms`, animationFillMode: 'backwards' }}
                      >
                        <div className="bg-white rounded-2xl px-6 py-5 w-full">
                          <p className="text-sm text-gray-800 mb-4" style={{ fontFamily: "Lexend Deca" }}>
                            {message.content}
                          </p>
                          
                          {/* Lesson Title */}
                          <h3 className="text-xl font-semibold mb-1" style={{ fontFamily: "Lexend Deca" }}>
                            {message.lessonPlan.plan.lesson_title}
                          </h3>
                          <p className="text-sm text-gray-600 mb-3">{message.lessonPlan.plan.subtitle}</p>
                          <p className="text-xs text-gray-500 mb-4">
                            ~{message.lessonPlan.intent_analysis.estimated_duration} minutes
                          </p>
                          
                          {/* Learn Section - Concepts */}
                          <div className="mb-4">
                            <h4 className="text-sm italic text-gray-500 mb-2">Learn</h4>
                            <div className="space-y-2">
                              {message.lessonPlan.plan.concepts.map((concept) => (
                                <div key={concept.id} className="bg-gray-50 rounded-lg p-3 hover:bg-gray-100 transition-colors">
                                  <p className="font-medium text-sm">{concept.title}</p>
                                  <p className="text-xs text-gray-600">{concept.focus}</p>
                                  <p className="text-xs text-gray-500 mt-1">~{concept.minutes} min</p>
                                </div>
                              ))}
                            </div>
                          </div>
                          
                          {/* Practice Section */}
                          <div className="mb-4">
                            <h4 className="text-sm italic text-gray-500 mb-2">Practice</h4>
                            <div className="flex gap-3 text-xs text-gray-600">
                              <span>{message.lessonPlan.plan.faq_count} FAQs</span>
                              <span>{message.lessonPlan.plan.spot_mistake_count} Spot-the-Mistake</span>
                              <span>{message.lessonPlan.plan.match_pair_count} Match & Pair</span>
                            </div>
                          </div>
                          
                          {/* Test Section */}
                          <div className="mb-5">
                            <h4 className="text-sm italic text-gray-500 mb-2">Test Your Knowledge</h4>
                            <div className="flex gap-3 text-xs text-gray-600">
                              <span>{message.lessonPlan.plan.mcq_count} MCQ</span>
                              <span>{message.lessonPlan.plan.fill_blank_count} Fill Blanks</span>
                              <span>{message.lessonPlan.plan.true_false_count} True/False</span>
                            </div>
                          </div>
                          
                          {/* Start Learning Button */}
                          {lessonPlanPreview && !isAccepting && (
                            <button
                              onClick={handleLessonAccept}
                              className="w-full bg-[#27c840] hover:bg-[#20a535] text-white font-medium py-2.5 px-4 rounded-lg transition-all duration-200 hover:shadow-md active:scale-[0.98]"
                              style={{ fontFamily: "Lexend Deca" }}
                            >
                              {topicId ? "Add lesson" : "Start Learning"}
                            </button>
                          )}
                        </div>
                      </div>
                    ) : null
                  )}

                  {isGenerating && (
                    <div className="flex justify-start animate-in fade-in slide-in-from-bottom-4 duration-500">
                      <div className="bg-white rounded-2xl px-5 py-4 flex items-center gap-3">
                        <Brain className="w-5 h-5 text-gray-500 animate-pulse" />
                        <div>
                          <p className="text-sm font-semibold text-gray-800" style={{ fontFamily: "Lexend Deca" }}>
                            {statusMessage || "Thinking..."}
                          </p>
                          <p className="text-xs text-gray-500">Gathering the best lessons for you</p>
                        </div>
                      </div>
                    </div>
                  )}

                  {isAccepting && (
                    <div className="flex justify-start animate-in fade-in slide-in-from-bottom-4 duration-500">
                      <div className="bg-white rounded-2xl px-5 py-4 flex items-center gap-3">
                        <Loader2 className="w-5 h-5 text-gray-500 animate-spin" />
                        <div>
                          <p className="text-sm font-semibold text-gray-800" style={{ fontFamily: "Lexend Deca" }}>
                            {acceptStatus || "Preparing your topic..."}
                          </p>
                          <p className="text-xs text-gray-500">This may take a few moments</p>
                        </div>
                      </div>
                    </div>
                  )}

                  {acceptError && (
                    <div className="flex justify-start animate-in fade-in slide-in-from-bottom-4 duration-500">
                      <div className="bg-white rounded-2xl px-5 py-4 flex items-center gap-3">
                        <AlertCircle className="w-5 h-5 text-red-500" />
                        <p className="text-sm text-red-600">{acceptError}</p>
                      </div>
                    </div>
                  )}

              <div ref={messagesEndRef} />
            </div>
          </div>
          
          {/* Fade gradient overlay */}
          <div className="absolute bottom-0 left-0 right-0 h-32 pointer-events-none bg-gradient-to-t from-white via-white/60 to-transparent" />
        </div>

        {/* Fixed Input Footer */}
        <div className="bg-white py-4 flex-shrink-0 flex justify-center relative">
          <div className="w-full max-w-4xl px-6">
            <SimpleChatInput onSend={handleChatSend} isGenerating={isGenerating} />
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default TopicOnboardingModal;
